import { memo, useCallback, useRef } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Link as ChakraLink,
  ListItem,
  Stack,
  Text,
  UnorderedList,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { TermsList, VideoPlayer } from '../components';
import { addSubmissionToHoop } from '../services/firestore';
import { uploadFileToStorage } from '../services/storage';
import { getVideoDuration, roundPretty } from '../utils';
import { maxVideoDurationSeconds, maxVideoSizeInMb } from '../config';

const initialValues = {
  name: '',
  email: '',
  file: '',
  terms: false,
};

const UploadDrawer = memo(({ isOpen, onClose, hoopId, hoopName }) => {
  const toast = useToast();
  const fileInputRef = useRef();

  const formik = useFormik({
    validateOnBlur: false,
    initialValues,
    onSubmit: async (values) => {
      if (!values.file || !values.email || !values.name || !values.terms) return;

      const result = await uploadFileToStorage({ hoopId, file: values.file, email: values.email });

      const newSubmission = {
        contributorName: values.name,
        contributorEmail: values.email,
        fileName: values.file.name,
        fileType: values.file.type,
        storageFileUrl: result.storageFileUrl,
        submissionDate: new Date(),
        hoopName,
        verified: false,
      };

      await addSubmissionToHoop({ hoopId, newSubmission });

      toast({
        title: 'Thanks for hooping!',
        description: (
          <>
            We&apos;ve got your video. Check back later to see your bucket on the map. Keep hooping,
            there is no limit to how many buckets you can get.
          </>
        ),
        status: 'success',
        position: 'top',
        variant: 'subtle',
        duration: null,
        isClosable: true,
        containerStyle: {
          fontSize: '18px',
          paddingTop: '24px',
          paddingBottom: '24px',
        },
      });

      formik.resetForm(initialValues);
      onClose();
    },
  });

  const handleRemoveFile = useCallback(() => {
    formik.setFieldValue('file', initialValues.file);

    if (fileInputRef?.current) fileInputRef.current.value = '';
  }, [formik]);

  const handleClose = useCallback(() => {
    formik.resetForm(initialValues);
    onClose();
  }, [formik, onClose]);

  const handleFileChange = useCallback(
    async (event) => {
      if (event.target) {
        const file = event.target.files?.[0];
        if (!file) return;

        // validate file size
        const sizeInMb = file.size / (1024 * 1024);

        if (sizeInMb > maxVideoSizeInMb) {
          await formik.setFieldValue('file', initialValues.file);
          if (fileInputRef?.current) fileInputRef.current.value = '';
          formik.setFieldError(
            'file',
            `File must be less than ${maxVideoSizeInMb} MB in size. Your file is ${roundPretty(
              sizeInMb,
            )} MB.`,
          );

          return;
        }

        // validate file duration
        const duration = await getVideoDuration(file);

        if (duration > maxVideoDurationSeconds) {
          await formik.setFieldValue('file', initialValues.file);
          if (fileInputRef?.current) fileInputRef.current.value = '';
          formik.setFieldError(
            'file',
            `File must be less than ${maxVideoDurationSeconds} seconds long. Your file is ${roundPretty(
              duration,
            )} seconds.`,
          );
        } else {
          // passed validation, set the file
          formik.setFieldValue('file', file);
        }
      }
    },
    [formik],
  );

  return (
    <Drawer
      isOpen={isOpen}
      id="upload-drawer"
      size="lg"
      placement="right"
      scrollBehavior="inside"
      motionPreset="slideInRight"
      returnFocusOnClose={false}
      autoFocus={false}
      onClose={handleClose}
      allowPinchZoom
      preserveScrollBarGap
    >
      <DrawerOverlay />
      <DrawerContent backgroundColor="blackAlpha.800" color="white">
        <DrawerHeader>Submit your video at {hoopName}</DrawerHeader>
        <DrawerBody>
          <form onSubmit={formik.handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired isDisabled={formik.isSubmitting}>
                <FormLabel htmlFor="name">Name</FormLabel>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                />
                <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
              </FormControl>
              <FormControl isRequired isDisabled={formik.isSubmitting}>
                <FormLabel htmlFor="email">Email Address</FormLabel>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  onChange={formik.handleChange}
                  value={formik.values.email}
                />
                <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
              </FormControl>
              <FormControl
                isRequired
                isInvalid={formik.errors.file || !formik.values.file}
                isDisabled={formik.isSubmitting}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <FormLabel htmlFor="file">File</FormLabel>

                  {formik.values.file && (
                    <Button size="xs" variant="ghost" color="red.500" onClick={handleRemoveFile}>
                      Remove File
                    </Button>
                  )}
                </Stack>
                <Box
                  className="file-drop-container"
                  borderRadius="md"
                  borderWidth={formik.values.file ? '0px' : '1px'}
                  borderStyle="dashed"
                  maxH="30vh"
                >
                  <Input
                    type="file"
                    id="file"
                    name="file"
                    accept="video/*"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                  {formik.values.file ? (
                    <VideoPlayer
                      autoPlay
                      controls={false}
                      aspectRatio={16 / 9}
                      objectFit="cover"
                      src={URL.createObjectURL(formik.values.file)}
                      type={formik.values.file.type}
                    />
                  ) : (
                    <VStack py={16} spacing={4}>
                      <FontAwesomeIcon size="3x" icon={['fas', 'file-video']} />
                      <Text fontSize="sm">Click to select, or drag and drop a video</Text>
                    </VStack>
                  )}
                </Box>
                <FormErrorMessage>{formik.errors.file}</FormErrorMessage>
                <FormHelperText>File requirements:</FormHelperText>
                <FormHelperText fontSize="xs">
                  <UnorderedList>
                    <ListItem>Less than {maxVideoSizeInMb} MB in size</ListItem>
                    <ListItem>Less than {maxVideoDurationSeconds} seconds in length</ListItem>
                  </UnorderedList>
                </FormHelperText>
              </FormControl>

              <FormControl isRequired isDisabled={formik.isSubmitting}>
                <FormLabel htmlFor="name" mt={2}>
                  Terms & Conditions
                </FormLabel>
                <Checkbox
                  id="terms"
                  name="terms"
                  onChange={formik.handleChange}
                  value={formik.values.terms}
                >
                  <Text fontWeight={500}>
                    I agree to the{' '}
                    <ChakraLink
                      as={ReactRouterLink}
                      to="/terms-and-conditions"
                      target="_blank"
                      color="blue.500"
                    >
                      terms and conditions
                      <ExternalLinkIcon ml="4px" verticalAlign="sub" />
                    </ChakraLink>
                  </Text>
                </Checkbox>

                <Box mt={2}>
                  <TermsList />
                </Box>
              </FormControl>
            </VStack>
            <Stack
              direction="row"
              justifyContent="space-between"
              width="100%"
              alignItems="center"
              mt={8}
              mb={2}
            >
              <Button
                onClick={handleClose}
                leftIcon={<FontAwesomeIcon icon={['fas', 'arrow-left']} />}
                iconSpacing={4}
                colorScheme="whiteAlpha"
                size="lg"
                variant="ghost"
                rounded="full"
              >
                Back
              </Button>

              <Button
                type="submit"
                colorScheme="knicks"
                color="black"
                size="lg"
                disabled={formik.submitCount > 0}
                isLoading={formik.isSubmitting}
                loadingText="Submitting..."
              >
                Submit
              </Button>
            </Stack>
          </form>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
});

UploadDrawer.displayName = 'UploadDrawer';

export default UploadDrawer;
